import 'flag-icons/css/flag-icons.min.css';

import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { all } from 'locale-codes';
import { useAppStoreSelector } from '../../../store';
import { useToast } from '../../general/components/Toast.hook';
import axios from '../../../utils/api/axiosInstance';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

import { useTheme } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';

import { GridActionsCellItem, DataGridPro } from '@mui/x-data-grid-pro';

import type { GridColDef, GridPaginationModel, GridSortModel, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

interface Language {
  LanguageID: number;
  ParentID?: number;
  LangCode: string;
  LangName: string;
  IsDefault: number;
  created_by: string;
  updated_by: string;
  created_on: string;
  modified_on: string;
}

interface Locale {
  name: string;
  local: string;
  location: string | null;
  tag: string;
  lcid: number;
  'iso639-2': string;
  'iso639-1': string;
}

interface LanguagesTableProps {
  languages: Language[];
}

function LanguagesTable({ languages: initialLanguages }: LanguagesTableProps) {
  const { t } = useTranslation();
  const userState = useAppStoreSelector((state) => state.user);
  const [triggerMessage] = useToast();
  const theme = useTheme();

  const [languages, setLanguages] = useState<Language[]>(initialLanguages);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'LanguageID', sort: 'asc' }]);

  // column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    LanguageID: true,
    LangCode: true,
    LangName: true,
    IsDefault: true,
    ParentID: true,
    created_by: false,
    created_on: false,
    updated_by: false,
    modified_on: true,
    actions: true,
  });

  const [openAddModal, setOpenAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLocales, setFilteredLocales] = useState<Locale[]>([]);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);
  const [editForm, setEditForm] = useState({
    langName: '',
    isDefault: false,
    parentId: '',
  });

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletingLanguage, setDeletingLanguage] = useState<Language | null>(null);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  // helper function to get country code from locale tag
  const getCountryCode = (localeTag: string): string | null => {
    const parts = localeTag.split('-');
    if (parts.length > 1) return parts[parts.length - 1].toLowerCase();
    return null;
  };

  // helper function to render flag
  const renderFlag = useCallback(
    (localeTag: string, size: 'small' | 'medium' = 'medium') => {
      const countryCode = getCountryCode(localeTag);
      const sizeStyles = size === 'small'
        ? { width: '20px', height: '14px', fontSize: '1rem' }
        : { width: '24px', height: '16px', fontSize: '1.2rem' };

      if (countryCode) {
        return (
          <Box
            component="span"
            className={`fi fi-${countryCode}`}
            sx={{
              ...sizeStyles,
              borderRadius: '2px',
              overflow: 'hidden',
              display: 'inline-block',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
            onError={(e) => {
              // fallback globe emoji
              e.currentTarget.style.display = 'none';
              const globeElement = document.createElement('span');
              globeElement.textContent = '🌐';
              globeElement.style.fontSize = size === 'small' ? '14px' : '16px';
              e.currentTarget.parentNode?.appendChild(globeElement);
            }}
          />
        );
      } else {
        // fallback globe emoji
        return (
          <Box
            component="span"
            sx={{
              ...sizeStyles,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: size === 'small' ? '14px' : '16px'
            }}
          >
            🌐
          </Box>
        );
      }
    },
    []
  );

  const isDefaultLanguage = useCallback(
    (langCode: string): boolean => {
      return languages.some(lang => lang.LangCode === langCode && lang.IsDefault === 1);
    },
    [languages]
  );

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
    setSearchTerm('');
    setFilteredLocales([]);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    setSearchTerm('');
    setFilteredLocales([]);
  };

  const handleOpenEditModal = (language: Language) => {
    setEditingLanguage(language);
    setEditForm({
      langName: language.LangName,
      isDefault: language.IsDefault === 1,
      parentId: language.ParentID?.toString() || ''
    });
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingLanguage(null);
    setEditForm({
      langName: '',
      isDefault: false,
      parentId: ''
    });
  };

  const handleEditFormChange = (field: string, value: any) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (editingLanguage) {
      try {
        setIsLoading(true);
        setLoadingMessage("Saving language changes...");
        const username = userState.data.user?.username || "unknown";

        const response = await axios.post('/api/mdm/config/language/update', {
          languageId: editingLanguage.LanguageID,
          LangName: editForm.langName,
          IsDefault: editForm.isDefault ? 1 : 0,
          ParentID: editForm.parentId ? parseInt(editForm.parentId) : undefined,
          updated_by: username
        });

        if (response.data && response.data.success) {
          setLanguages(prevLanguages =>
            prevLanguages.map(lang => {
              // for the edited language
              if (lang.LanguageID === editingLanguage.LanguageID) {
                return {
                  ...lang,
                  LangName: editForm.langName,
                  IsDefault: editForm.isDefault ? 1 : 0,
                  ParentID: editForm.parentId ? parseInt(editForm.parentId) : undefined,
                  updated_by: username,
                  modified_on: new Date().toISOString()
                };
              }
              // if this language is being set as default, update the others
              else if (editForm.isDefault) {
                return {
                  ...lang,
                  IsDefault: 0
                };
              }
              return lang;
            })
          );

          triggerMessage(t('mdm.languageUpdatedSuccess'), false);
        } else {
          triggerMessage(response.data.message || t('mdm.languageUpdateError'), true);
        }
      } catch (error) {
        console.error('Error updating language:', error);
        triggerMessage(t('mdm.languageUpdateError'), true);
      } finally {
        setIsLoading(false);
        setLoadingMessage(null);
        handleCloseEditModal();
      }
    }
  };

  const handleDeleteClick = (language: Language) => {
    setDeletingLanguage(language);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (deletingLanguage) {
      try {
        setIsLoading(true);
        setLoadingMessage(`Deleting language ${deletingLanguage.LangCode}...`);
        const username = userState.data.user?.username || "unknown";

        // dont allow deleting the default language
        if (deletingLanguage.IsDefault === 1) {
          triggerMessage(t('mdm.cannotDeleteDefaultLanguage'), true);
          setIsLoading(false);
          return;
        }

        const response = await axios.post('/api/mdm/config/language/delete', {
          languageId: deletingLanguage.LanguageID,
          deleted_by: username
        });

        if (response.data && response.data.success) {
          setLanguages(prevLanguages =>
            prevLanguages.filter(lang => lang.LanguageID !== deletingLanguage.LanguageID)
          );

          triggerMessage(t('mdm.languageDeletedSuccess'), false);
        } else {
          triggerMessage(response.data.message || t('mdm.languageDeleteError'), true);
        }
      } catch (error) {
        console.error('Error deleting language:', error);
        triggerMessage(t('mdm.languageDeleteError'), true);
      } finally {
        setIsLoading(false);
        setLoadingMessage(null);
        setOpenDeleteDialog(false);
        setDeletingLanguage(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDeletingLanguage(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    // start search with at least 2 chars
    if (value.trim().length < 2) {
      setFilteredLocales([]);
    } else {
      // get the existing language codes to filter them out
      const existingLanguageCodes = languages.map(lang => lang.LangCode.toLowerCase());

      const filtered = (all as Locale[]).filter((locale: Locale) =>
        locale.location !== null &&
        !existingLanguageCodes.includes(locale.tag.toLowerCase()) && (
          locale.name.toLowerCase().includes(value.toLowerCase()) ||
          locale.location.toLowerCase().includes(value.toLowerCase()) ||
          locale.tag.toLowerCase().includes(value.toLowerCase())
        )
      );
      // show first 10 results
      setFilteredLocales(filtered.slice(0, 10));
    }
  };

  const handleLocaleSelect = async (locale: Locale) => {
    try {
      setIsLoading(true);
      setLoadingMessage(`Adding language ${locale.tag} (${locale.local || locale.name}) and initializing in all tables...`);
      const username = userState.data.user?.username || "unknown";

      const response = await axios.post('/api/mdm/config/language/add', {
        LangCode: locale.tag,
        LangName: locale.local || locale.name,
        ParentID: undefined,
        IsDefault: 0,
        created_by: username
      });

      if (response.data && response.data.success) {
        if (response.data.data) {
          setLanguages(prevLanguages => {
            const newLanguage = response.data.data;
            // if the new language is set as default, update all others
            if (newLanguage.IsDefault === 1) {
              return [
                ...prevLanguages.map(lang => ({ ...lang, IsDefault: 0 })),
                newLanguage
              ];
            } else {
              return [...prevLanguages, newLanguage];
            }
          });
          triggerMessage(t('mdm.languageAddedSuccess'), false);
        } else {
          // fallback reload the data
          triggerMessage(t('mdm.languageAddSuccess'), false);
          const refreshedData = await axios.get('/api/mdm/config/getLanguages');
          if (refreshedData.data && refreshedData.data.success) {
            setLanguages(refreshedData.data.data);
          }
        }
      } else {
        triggerMessage(response.data.message || t('mdm.languageAddError'), true);
      }
    } catch (error) {
      console.error('Error adding language:', error);
      triggerMessage(t('mdm.languageAddError'), true);
    } finally {
      setIsLoading(false);
      setLoadingMessage(null);
      handleCloseAddModal();
    }
  };

  const columns = useMemo<GridColDef[]>(() => [
    { field: 'LanguageID', headerName: 'ID', flex: 0.4, minWidth: 80, sortable: true },
    {
      field: 'LangCode',
      headerName: 'Language Code',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
          {renderFlag(params.value as string, 'small')}
          <Chip label={params.value as string} size="small" color="primary" variant="outlined" />
          {isDefaultLanguage(params.value as string) && (
            <Tooltip title="Default language" arrow>
              <StarIcon sx={{ color: 'gold', fontSize: '1rem' }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    { field: 'LangName', headerName: 'Language Name', flex: 1, minWidth: 180, sortable: true },
    { field: 'ParentID', headerName: 'Parent ID', flex: 0.7, minWidth: 120, valueFormatter: (value) => value ?? '-', sortable: true },
    // { field: 'created_by', headerName: 'Created By', flex: 1, sortable: true },
    // { field: 'created_on', headerName: 'Created On', flex: 1, sortable: true, valueFormatter: (value) => formatDate(value as string) },
    // { field: 'updated_by', headerName: 'Updated By', flex: 1, sortable: true },
    { field: 'modified_on', headerName: 'Modified On', flex: 0.6, minWidth: 160, sortable: true, valueFormatter: (value) => formatDate(value as string) },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('mdm.actions'),
      flex: 0.6,
      minWidth: 120,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon fontSize="small" sx={{ color: 'primary.main' }} />}
          label={t('mdm.edit')}
          onClick={() => handleOpenEditModal(params.row as Language)}
          showInMenu={false}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />}
          label={t('mdm.delete')}
          onClick={() => handleDeleteClick(params.row as Language)}
          showInMenu={false}
        />,
      ],
      sortable: false,
      filterable: false,
    },
  ], [renderFlag, t, isDefaultLanguage, theme]);

  const rows = useMemo(
    () => languages.map((l) => ({ id: l.LanguageID, ...l })),
    [languages]
  );

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {isLoading && !openAddModal && !openEditModal && !openDeleteDialog && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            zIndex: 10,
          }}
        >
          <CircularProgress />
          {loadingMessage && (
            <Typography variant="h6" color="primary">
              {loadingMessage}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Box sx={{ mb: 1, mt: '-8px', ml: '40px' }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t('mdm.languagesManagement')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="contained" color="primary" onClick={handleOpenAddModal}>
            {t('mdm.addLanguage')}
          </Button>
        </Box>
        <Box sx={{ position: 'absolute', left: 0, right: 0 }}>
          <DataGridPro
            sx={{
              mb: 1,
              minWidth: '400px',
              minHeight: '232px',
              maxHeight: 'calc(100vh - 300px)',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              }
            }}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={(m) => setPaginationModel(m)}
            pageSizeOptions={[5, 10]}
            disableRowSelectionOnClick
            rows={rows}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(m) => setColumnVisibilityModel(m)}
            sortingOrder={["asc", "desc"]}
            sortModel={sortModel}
            onSortModelChange={(m) => setSortModel(m)}
            slots={{
                noRowsOverlay: () => (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color={theme.palette.text.secondary}>
                      {t('mdm.noLanguagesFound')}
                    </Typography>
                  </Box>
                ),
              }}
          />
        </Box>
      </Box>

      {/* Add Language Modal */}
      <Dialog open={openAddModal} onClose={handleCloseAddModal} maxWidth="md" fullWidth>
        {isLoading && openAddModal && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              zIndex: 2000
            }}
          >
            <CircularProgress size={60} />
            {loadingMessage && (
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', textAlign: 'center', px: 3 }}>
                {loadingMessage}
              </Typography>
            )}
          </Box>
        )}
        <DialogTitle>{t('mdm.addNewLanguage')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('mdm.searchLanguages')}
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {searchTerm.trim().length < 2 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: theme.palette.text.secondary }}>
                <Typography variant="body2">{t('mdm.typeToSearch')}</Typography>
              </Box>
            ) : filteredLocales.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: theme.palette.text.secondary }}>
                <Typography variant="body2">
                  {searchTerm.length > 0 && (all as Locale[]).some((locale) =>
                    locale.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    locale.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    locale.tag.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                    ? t('mdm.languagesAlreadyAdded', { searchTerm })
                    : t('mdm.noLanguagesFound', { searchTerm })}
                </Typography>
              </Box>
            ) : (
              <>
                <List>
                  {filteredLocales.map((locale, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemButton onClick={() => handleLocaleSelect(locale)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ mr: 2 }}>{renderFlag(locale.tag, 'small')}</Box>
                          <ListItemText
                            primary={
                              <>
                                <strong>{locale.local || locale.name}</strong>
                                {locale.local && locale.name !== locale.local && (
                                  <span style={{ opacity: 0.7 }}> ({locale.name})</span>
                                )}
                                {locale.location && <span> - {locale.location}</span>}
                              </>
                            }
                            secondary={locale.tag}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                {filteredLocales.length === 10 && (
                  <Box sx={{ textAlign: 'center', py: 2, color: theme.palette.text.secondary }}>
                    <Typography variant="caption">{t('mdm.showingFirstResults')}</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal}>{t('mdm.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Language Modal */}
      <Dialog open={openEditModal} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
        {isLoading && openEditModal && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              zIndex: 2000
            }}
          >
            <CircularProgress size={60} />
            {loadingMessage && (
              <Typography variant="h5" color={theme.palette.text.primary} sx={{ fontWeight: 'bold', textAlign: 'center', px: 3 }}>
                {loadingMessage}
              </Typography>
            )}
          </Box>
        )}
        <DialogTitle>
          {editingLanguage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {renderFlag(editingLanguage.LangCode, 'small')}
              <Typography variant="h6">{t('mdm.editLanguage')}: {editingLanguage.LangName}</Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              {t('mdm.languageNameWarning')}
            </Alert>

            <TextField
              fullWidth
              label={t('mdm.languageName')}
              value={editForm.langName}
              onChange={(e) => handleEditFormChange('langName', e.target.value)}
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Checkbox checked={editForm.isDefault} onChange={(e) => handleEditFormChange('isDefault', e.target.checked)} />}
                label={t('mdm.setAsDefault')}
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('mdm.parentLanguage')}</InputLabel>
              <Select value={editForm.parentId} onChange={(e) => handleEditFormChange('parentId', e.target.value)} label={t('mdm.parentLanguage')}>
                <MenuItem value="">
                  <em>{t('mdm.noParent')}</em>
                </MenuItem>
                {languages
                  .filter((lang) => lang.LanguageID !== editingLanguage?.LanguageID)
                  .sort((a, b) => a.LanguageID - b.LanguageID)
                  .map((lang) => (
                    <MenuItem key={lang.LanguageID} value={lang.LanguageID.toString()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {renderFlag(lang.LangCode, 'small')}
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">
                            {lang.LangName} [ID: {lang.LanguageID}]
                          </Typography>
                          <Typography variant="caption" color={theme.palette.text.secondary}>
                            {lang.LangCode}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>{t('mdm.cancel')}</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            {t('mdm.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        {isLoading && openDeleteDialog && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              zIndex: 2000,
            }}
          >
            <CircularProgress size={60} />
            {loadingMessage && (
              <Typography variant="h5" color={theme.palette.text.primary} sx={{ fontWeight: 'bold', textAlign: 'center', px: 3 }}>
                {loadingMessage}
              </Typography>
            )}
          </Box>
        )}
        {deletingLanguage && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {renderFlag(deletingLanguage.LangCode, 'small')}
                <Typography variant="h6">{t('mdm.deleteLanguage')}: {deletingLanguage.LangName}</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('mdm.deleteWarning')}
              </Alert>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                {t('mdm.deleteConfirmation', { languageName: deletingLanguage.LangName, languageCode: deletingLanguage.LangCode })}
                {deletingLanguage.IsDefault === 1 && (
                  <Box sx={{ mt: 1, fontWeight: 'bold', color: theme.palette.error.main }}>
                    {t('mdm.defaultLanguageWarning')}
                  </Box>
                )}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeleteCancel}>{t('mdm.cancel')}</Button>
              <Button onClick={handleDeleteConfirm} variant="contained" color="error">
                {t('mdm.deleteLanguageButton')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default LanguagesTable;
